<script setup lang="ts">
  // Native counterpart of V2PrinterAssignmentPicker: same chips + "Reemplaza
  // a X" warnings, computed over the NAME-based native store (live refs
  // falling back to persisted names so undiscovered ghosts still warn).
  // Deliberately independent from the desktop picker — that one is
  // bridge-bound and production-critical; do not extract/share.
  import { computed } from 'vue';
  import {
    INativeDestinationSelection,
    TNativeDestination,
    useNativePrintingUi,
  } from '@/composition/printing/use-native-printing-ui';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';

  interface IDestinationOption extends INativeDestinationSelection {
    key: string;
    /** Station name for comanda; ticket/sticker label via i18n. */
    label: string;
    checked: boolean;
    /** Name of the printer this selection would replace (if any). */
    replaces: string | null;
  }

  const props = withDefaults(
    defineProps<{
      value: INativeDestinationSelection[];
      /** Wizard type filtering (escpos → ticket+comanda, zpl → sticker). */
      allowedDestinations?: TNativeDestination[];
      /** Printer being edited — its own current assignments never warn. */
      selfName?: string | null;
      disabled?: boolean;
    }>(),
    {
      allowedDestinations: () => ['ticket', 'comanda', 'sticker'],
      selfName: null,
      disabled: false,
    }
  );

  const emit = defineEmits<{
    (e: 'input', value: INativeDestinationSelection[]): void;
  }>();

  const ui = useNativePrintingUi();

  function isChecked(destination: TNativeDestination, stationId?: number) {
    return props.value.some(
      (s) => s.destination === destination && s.stationId === stationId
    );
  }

  function replacedBy(
    destination: TNativeDestination,
    stationId?: number
  ): string | null {
    const current = ui.assignedNameOf(destination, stationId);
    if (!current || current === props.selfName) return null;
    return current;
  }

  const options = computed<IDestinationOption[]>(() => {
    const result: IDestinationOption[] = [];

    if (props.allowedDestinations.includes('ticket')) {
      result.push({
        key: 'ticket',
        destination: 'ticket',
        label: '',
        checked: isChecked('ticket'),
        replaces: isChecked('ticket') ? replacedBy('ticket') : null,
      });
    }

    if (props.allowedDestinations.includes('comanda')) {
      for (const station of ui.restaurantStore.printStations) {
        result.push({
          key: `comanda:${station.stationId}`,
          destination: 'comanda',
          stationId: station.stationId,
          label: station.name,
          checked: isChecked('comanda', station.stationId),
          replaces: isChecked('comanda', station.stationId)
            ? replacedBy('comanda', station.stationId)
            : null,
        });
      }
    }

    if (props.allowedDestinations.includes('sticker')) {
      result.push({
        key: 'sticker',
        destination: 'sticker',
        label: '',
        checked: isChecked('sticker'),
        replaces: isChecked('sticker') ? replacedBy('sticker') : null,
      });
    }

    return result;
  });

  function toggle(option: IDestinationOption) {
    if (props.disabled) return;
    if (option.checked) {
      emit(
        'input',
        props.value.filter(
          (s) =>
            !(
              s.destination === option.destination &&
              s.stationId === option.stationId
            )
        )
      );
    } else {
      emit('input', [
        ...props.value,
        { destination: option.destination, stationId: option.stationId },
      ]);
    }
  }
</script>

<template>
  <div class="v2-native-assignment-picker" :class="{ 'is-disabled': disabled }">
    <div class="picker-chips">
      <button
        v-for="option in options"
        :key="option.key"
        type="button"
        class="picker-chip"
        :class="{ checked: option.checked }"
        :disabled="disabled"
        @click="toggle(option)"
      >
        <PosUiIcon
          :name="option.checked ? 'checkbox-marked' : 'checkbox-blank-outline'"
          class="picker-chip-icon"
        />
        <template v-if="option.destination === 'ticket'">
          {{ $t('printingV2.destTicket') }}
        </template>
        <template v-else-if="option.destination === 'sticker'">
          {{ $t('printingV2.destSticker') }}
        </template>
        <template v-else>{{ option.label }}</template>
      </button>
    </div>

    <div
      v-for="option in options.filter((o) => o.replaces)"
      :key="`warn-${option.key}`"
      class="picker-warning"
    >
      <PosUiIcon name="alert" class="picker-warning-icon" />
      {{ $t('printingV2.replaceWarning', { name: option.replaces }) }}
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-native-assignment-picker {
    @apply tw-flex tw-flex-col tw-gap-2;

    &.is-disabled {
      @apply tw-opacity-60;
    }

    .picker-chips {
      @apply tw-flex tw-flex-wrap tw-gap-2;

      .picker-chip {
        @apply tw-flex tw-min-h-[40px] tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-neutral-300 tw-bg-white tw-px-3 tw-text-sm tw-font-medium tw-text-neutral-600 tw-transition-colors hover:tw-bg-neutral-50;

        &.checked {
          @apply tw-border-orange-600 tw-bg-orange-50 tw-text-orange-700;
        }

        &:disabled {
          @apply tw-cursor-not-allowed;
        }

        .picker-chip-icon {
          @apply tw-text-lg;
        }
      }
    }

    .picker-warning {
      @apply tw-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-bg-amber-50 tw-px-3 tw-py-2 tw-text-xs tw-text-amber-700;

      .picker-warning-icon {
        @apply tw-text-base;
      }
    }
  }
</style>
